import { api } from '../../../lib/api';

export interface StaffMember {
  id: string;
  email: string;
  name: string;
  role: 'agent' | 'supervisor' | 'admin';
  phone?: string;
  department?: string;
  avatarUrl?: string;
  storeId: string;
  active: boolean;
  online: boolean;
  lastSeen?: string;
  notifyOnNewSession: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PreferencesUpdate {
  notifyOnNewSession?: boolean;
}

export interface CreateStaffData {
  email: string;
  name: string;
  role: string;
  phone?: string;
  department?: string;
}

export interface UpdateStaffData {
  name?: string;
  role?: string;
  phone?: string;
  department?: string;
  active?: boolean;
}

function mapStaffResponse(data: any): StaffMember {
  return {
    id: data.id,
    email: data.email,
    name: data.name,
    role: data.role,
    phone: data.phone,
    department: data.department,
    avatarUrl: data.avatar_url,
    storeId: data.store_id,
    active: data.active,
    online: data.online,
    lastSeen: data.last_seen,
    notifyOnNewSession: !!data.notify_on_new_session,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export const staffService = {
  async listStaff(params?: {
    role?: string;
    active?: boolean;
    department?: string;
  }): Promise<StaffMember[]> {
    const queryParams = new URLSearchParams();
    if (params?.role) queryParams.set('role', params.role);
    if (params?.active !== undefined) queryParams.set('active', String(params.active));
    if (params?.department) queryParams.set('department', params.department);

    const query = queryParams.toString();
    const url = `/staff/${query ? `?${query}` : ''}`;
    const data = await api.get<any[]>(url);
    return data.map(mapStaffResponse);
  },

  async getStaff(staffId: string): Promise<StaffMember> {
    const data = await api.get<any>(`/staff/${staffId}`);
    return mapStaffResponse(data);
  },

  async createStaff(staffData: CreateStaffData): Promise<StaffMember> {
    const data = await api.post<any>('/staff/', staffData);
    return mapStaffResponse(data);
  },

  async updateStaff(staffId: string, staffData: UpdateStaffData): Promise<StaffMember> {
    const data = await api.patch<any>(`/staff/${staffId}`, staffData);
    return mapStaffResponse(data);
  },

  async deleteStaff(staffId: string): Promise<void> {
    await api.delete<void>(`/staff/${staffId}`);
  },

  async activateStaff(staffId: string): Promise<StaffMember> {
    const data = await api.post<any>(`/staff/${staffId}/activate`);
    return mapStaffResponse(data);
  },

  async deactivateStaff(staffId: string): Promise<StaffMember> {
    const data = await api.post<any>(`/staff/${staffId}/deactivate`);
    return mapStaffResponse(data);
  },

  async getMe(): Promise<StaffMember> {
    const data = await api.get<any>('/staff/me');
    return mapStaffResponse(data);
  },

  async updateMyPreferences(prefs: PreferencesUpdate): Promise<StaffMember> {
    const body: Record<string, unknown> = {};
    if (prefs.notifyOnNewSession !== undefined) {
      body.notify_on_new_session = prefs.notifyOnNewSession;
    }
    const data = await api.patch<any>('/staff/me', body);
    return mapStaffResponse(data);
  },
};
